import React, { Component } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Spin, Drawer, Button, Tooltip } from 'grab-ui';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_POI_SEARCH_EVENTS,
  SCRIBE_STATE_NAMES,
  SCRIBE_EVENT_PARAMS,
  ROUTE_OPTIMIZE_STATUS_VALUE,
  SCRIBE_HOME_EVENTS,
} from 'applications/scribe/instrumentations/track';
import {
  getCompany,
  getCompanyID,
  getUserSelectedCityCountryCode,
  getUserCountryCode,
} from 'helper/user';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import debounce from 'common/utils/debounce';
import _uniqueId from 'lodash/uniqueId';
import DragHandler from 'public/icons/drag-handle-dots.svg';
import {
  toggleLocationDrawerAction,
  locationDrawerFindShortestRouteAction,
  updateOptimizeQuotesStatusAction,
} from 'actions/app-actions';
import {
  locationDrawerShopifyRouteOptimizationAction,
  updateShopifyOptimizeQuotesStatusAction,
} from 'actions/shopify-actions';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import { userInfoSelector } from 'selectors/app-selector';
import reorder, { reorderByIndex, reverseReorderByIndex } from 'helper/reorder';
import Map from 'components/booking/map/map';
import { getFormattedAmount } from 'helper/quote';
import optimizeRouteIcon from 'public/icons/optimize-route.svg';
import circledCheckMark from 'public/icons/circled-check-mark.svg';
import {
  QUOTE_OPTIMIZED_ALLOW_REVERT,
  QUOTE_OPTIMIZED,
  QUOTE_OPTIMIZED_FAILED,
} from 'constants/booking-constants';
import { getOptimizedRecipientOrder } from 'helper/quoteV2-helper';
import AddressField from '../address-field/address-field';
import css from './route-optimization-location-drawer.less';
import ContextualOnboarding from '../contextual-onboarding';
import RouteOptimizationOnboarding from './route-optimization-onboarding';
import { Desktop } from '../responsive';
import {
  useLazyLoadLottie,
  ANIMATION_KEYS,
} from '../../../hooks/useLazyLoadLottie';

const cx = classnames.bind(css);

const addressFieldsContainerID = 'addressFieldsContainer';

// Wrapper component for Route Optimization animation hook
const RouteOptimizationAnimation = ({ onRef }) => {
  const {
    LottieComponent: ROAnimationData,
    isLoading: isROLoading,
  } = useLazyLoadLottie(ANIMATION_KEYS.ROUTE_OPTIMISATION, {
    ref: onRef,
    options: {
      loop: false,
      autoplay: false,
    },
    height: 18,
    width: 18,
  });

  if (isROLoading) {
    return null;
  }

  // Render the component with the ref already included via hook props
  return ROAnimationData ? <ROAnimationData /> : null;
};

RouteOptimizationAnimation.propTypes = {
  onRef: PropTypes.func,
};

RouteOptimizationAnimation.defaultProps = {
  onRef: () => {},
};

// Wrapper component for Driver Nearby animation hook
const DriverNearbyAnimation = () => {
  const {
    LottieComponent: DriverNearbyLottie,
    isLoading: DriverNearbyLottieLoading,
  } = useLazyLoadLottie(ANIMATION_KEYS.DRIVER_NEARBY, {
    options: {
      loop: true,
      autoplay: true,
    },
    height: 133,
    width: 172,
  });

  if (DriverNearbyLottieLoading) {
    return null;
  }

  return DriverNearbyLottie ? <DriverNearbyLottie /> : null;
};

class RouteOptimizationLocationDrawer extends Component {
  constructor(props) {
    super(props);
    const addressFields = this.initAddressFields(
      props.locationDrawer.locations,
    );
    this.state = {
      ...this.calculateDrawerSizeAndDirection(),
      ...this.defaultMapPin(addressFields),
      addressFields,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    this.setOptimizedQuoteState(prevProps);
    this.propagateLocations(prevState);
    this.trackRouteOptimizationResult(prevProps);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  onSelectAddress = (index, address) => {
    const { userInfo, locationDrawer: { isSender } } = this.props;
    const { addressFields } = this.state;
    const companyID = getCompanyID(userInfo);
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'POI_SELECT', {
      [custom.COMPANY_ID]: companyID,
      STEP: isSender ? 'PICKUP' : 'DROPOFF',
      POI_ID: address.id,
    });

    const newAddressFields = _cloneDeep(addressFields);
    newAddressFields[index].address = address;

    this.setState({
      mapPinIndex: index,
      addressFields: newAddressFields,
    });
    this.clearOptimizedQuote();
  };

  onFocus = index => () => {
    this.setState({
      mapPinIndex: index,
    });
  };

  getDisplayMapLatLng = () => {
    const { addressFields, mapPinIndex } = this.state;
    return _get(addressFields[mapPinIndex], 'address.latlng', {});
  };

  onDelete = index => {
    const { addressFields, mapPinIndex } = this.state;
    const newAddressFields = [...addressFields];
    newAddressFields.splice(index, 1);
    const newState = {
      addressFields: newAddressFields,
    };
    if (index === mapPinIndex || mapPinIndex >= newAddressFields.length) {
      Object.assign(newState, this.defaultMapPin(newAddressFields));
    }
    this.setState(newState);
    this.clearOptimizedQuote();
  };

  onDragEnd = result => {
    const {
      source: { index: sourceIndex },
      destination: { index: destIndex },
    } = result;
    const addressFields = reorder(
      this.state.addressFields,
      sourceIndex,
      destIndex,
    );
    this.setState({
      addressFields,
    });
    this.clearOptimizedQuote();
  };

  defaultMapPin = addressFields => ({
    mapPinIndex: addressFields.length > 0 ? addressFields.length - 1 : 0,
  });

  initAddressFields = locations => {
    if (!locations || _isEmpty(locations[0])) {
      return [this.createAddressFieldProps()];
    }

    const validLocations = locations.filter(l => !l.deleted);
    return validLocations.map(loc => this.createAddressFieldProps(loc));
  };

  // eslint-disable-next-line consistent-return
  setOptimizedQuoteState = prevProps => {
    const path = 'optimizeQuotesStatus.addressFieldsNeedReorder';
    const prevAddressFieldsNeedReorder = _get(prevProps, path);
    const currentAddressFieldsNeedReorder = _get(this.props, path);
    if (!prevAddressFieldsNeedReorder && currentAddressFieldsNeedReorder) {
      const { quoteV2 } = this.props;
      const optimizedRecipientOrder = getOptimizedRecipientOrder(quoteV2);
      this.reorderAddressFieldsAndUpdate(
        optimizedRecipientOrder,
        reorderByIndex,
      );
    }
  };

  clearOptimizedQuote = () => {
    const { updateOptimizeQuotesStatus } = this.props;
    updateOptimizeQuotesStatus({
      addressFieldsNeedReorder: false,
      status: null,
      previousQuoteForRender: null,
    });
  };

  // eslint-disable-next-line consistent-return
  reorderAddressFieldsAndUpdate = (newOrderOfAddreses, reorderFunc) => {
    const { addressFields } = this.state;
    const { updateOptimizeQuotesStatus } = this.props;
    const reorderedAddressFields = reorderFunc(
      addressFields.filter(af => af.address),
      newOrderOfAddreses,
    );
    this.setState({
      addressFields: reorderedAddressFields,
    });
    updateOptimizeQuotesStatus({ addressFieldsNeedReorder: false });
  };

  createAddressFieldProps = address => {
    const initialValue = !_isEmpty(address)
      ? {
          keywords: address.address.name,
          address:
            address.address.combinedAddress || address.address.combined_address,
        }
      : '';

    return {
      key: _uniqueId('af'),
      address,
      afProps: {
        initialValue,
      },
    };
  };

  handleResize = debounce(
    () => {
      this.setState(this.calculateDrawerSizeAndDirection());
    },
    150,
    false,
  );

  calculateDrawerSizeAndDirection = () => {
    if (window.innerWidth < 932) {
      return { width: '100%', placement: 'bottom' };
    }
    return { width: 495, placement: 'right' };
  };

  addStop = () => {
    const { userInfo } = this.props;
    const { addressFields } = this.state;
    const companyID = getCompanyID(userInfo);
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'ADD_STOP', {
      [custom.COMPANY_ID]: companyID,
    });

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_HOME_EVENTS.ADD_STOP,
      facets: {
        ...getCommonFacet(userInfo),
      },
    });

    this.setState(
      {
        addressFields: [...addressFields, this.createAddressFieldProps()],
      },
      () => {
        // after adding a field, scroll to bottom
        const afs = document.getElementById(addressFieldsContainerID);
        if (afs) {
          afs.scrollTop = afs.scrollHeight;
        }
      },
    );
  };

  propagateLocations = prevState => {
    const { onUpdate } = this.props;
    const { addressFields } = this.state;
    if (prevState.addressFields !== addressFields) {
      const oldIndexMap = {};
      prevState.addressFields
        .map(af => af.address)
        .filter(adr => !!adr)
        .forEach((adr, index) => {
          oldIndexMap[adr.id] = index;
        });

      const newRecipientLocations = addressFields
        .map(af => af.address)
        .filter(adr => !!adr);
      // oldIndexes used to persist cod, name, note, phone of that recipient address
      const oldIndexes = newRecipientLocations.map(adr => oldIndexMap[adr.id]);
      onUpdate(newRecipientLocations, oldIndexes);
    }
  };

  playLottieAnimOnce = anim => {
    if (!anim) {
      return;
    }
    if (!window._roRottieAnimPlayed) {
      window._roRottieAnimPlayed = true;
      anim.play();
    }
  };

  confirmClicked = () => {
    const {
      toggleLocationDrawer,
      userInfo,
      locationDrawer: { isSender },
    } = this.props;
    const companyID = getCompanyID(userInfo);
    const poiIDs = this.state.addressFields.map(af => _get(af, 'address.id'));
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'CONFIRM', {
      [custom.COMPANY_ID]: companyID,
      STEP: isSender ? 'PICKUP' : 'DROPOFF',
      POI_ID: poiIDs,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.CONFIRM,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.STEP_TYPE]: isSender ? 'PICKUP' : 'DROPOFF',
        [isSender
          ? SCRIBE_EVENT_PARAMS.PICKUP_POI_ID
          : SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: poiIDs,
      },
    });
    toggleLocationDrawer([]);
  };

  optimizeRouteButtonAvailable = () => {
    const { order } = this.props;
    const senderLocation = _get(order, 'senderLocation');
    const recipientLocations = _get(order, 'recipientLocations', []);
    const quoteForRender = _get(order, 'quote.forRender');
    return senderLocation && recipientLocations.length > 1 && quoteForRender;
  };

  getRouteOptimizationTrackingFacets = () => {
    const { userInfo, order } = this.props;
    const senderLocation = _get(order, 'senderLocation');
    const recipientLocations = _get(order, 'recipientLocations', []);
    return {
      ...getCommonFacet(userInfo),
      [SCRIBE_EVENT_PARAMS.STEP_TYPE]: 'DROPOFF',
      [SCRIBE_EVENT_PARAMS.IS_MULTISTOP_BOOKING]: true,
      [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: _get(senderLocation, 'id'),
      [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: recipientLocations.map(location =>
        _get(location, 'id'),
      ),
    };
  };

  trackRouteOptimizationResult = prevProps => {
    const prevStatus = _get(prevProps, 'optimizeQuotesStatus.status');
    const curStatus = _get(this.props, 'optimizeQuotesStatus.status');
    if (prevStatus !== curStatus) {
      let result = null;
      switch (curStatus) {
        case QUOTE_OPTIMIZED:
          result = ROUTE_OPTIMIZE_STATUS_VALUE.DID_NOT_OPTIMIZE;
          break;
        case QUOTE_OPTIMIZED_ALLOW_REVERT:
          result = ROUTE_OPTIMIZE_STATUS_VALUE.OPTIMIZED_SUCCESS;
          break;
        case QUOTE_OPTIMIZED_FAILED:
          result = ROUTE_OPTIMIZE_STATUS_VALUE.ERROR;
          break;
        default:
          return;
      }
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
        eventName: SCRIBE_POI_SEARCH_EVENTS.FIND_SHORTEST_ROUTE_RESULTS,
        facets: {
          ...this.getRouteOptimizationTrackingFacets(),
          [SCRIBE_EVENT_PARAMS.ROUTE_OPTIMIZE_STATUS]: result,
        },
      });
    }
  };

  useOriginalRoute = () => {
    const { quoteV2 } = this.props;
    const optimizedRecipientOrder = getOptimizedRecipientOrder(quoteV2);
    this.reorderAddressFieldsAndUpdate(
      optimizedRecipientOrder,
      reverseReorderByIndex,
    );
    this.clearOptimizedQuote();

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.USE_ORIGINAL_ROUTE,
      facets: this.getRouteOptimizationTrackingFacets(),
    });
  };

  onFindShortestRoute = () => {
    const { findShortestRoute } = this.props;
    this.clearOptimizedQuote();
    findShortestRoute();

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.FIND_SHORTEST_ROUTE,
      facets: this.getRouteOptimizationTrackingFacets(),
    });
  };

  renderOptimizeRouteButton = () => {
    if (!this.optimizeRouteButtonAvailable()) {
      return null;
    }

    const optimizeQuotesStatus = _get(this.props, 'optimizeQuotesStatus', {});
    const { status } = optimizeQuotesStatus;

    if (status === QUOTE_OPTIMIZED_ALLOW_REVERT) {
      return (
        <Tooltip
          placement="bottomRight"
          title={I18n.t('v2.locationDrawer.tooltip.useOriginalRoute')}
        >
          <Button
            className={css.optimizeRouteButton}
            onClick={this.useOriginalRoute}
          >
            <span className={css.icon}>
              <img src={optimizeRouteIcon} alt={''} />
            </span>
            {I18n.t('v2.locationDrawer.useOriginalRoute')}
          </Button>
        </Tooltip>
      );
    }
    if (status === QUOTE_OPTIMIZED) {
      return (
        <Tooltip
          placement="bottomRight"
          title={I18n.t('v2.locationDrawer.tooltip.alreadyShortened')}
        >
          <Button className={cx('disabled', 'optimizeRouteButton')}>
            <span className={css.icon}>
              <img src={circledCheckMark} alt={''} />
            </span>
            {I18n.t('v2.locationDrawer.routeAlreadyShortened')}
          </Button>
        </Tooltip>
      );
    }
    return (
      <div className={css.optimizeRouteButton}>
        <Tooltip
          placement="bottomRight"
          title={I18n.t('v2.locationDrawer.tooltip.findShortestRoute')}
        >
          <Button id={'routeOptimizeButton'} onClick={this.onFindShortestRoute}>
            <span className={css.icon}>
              {!window._roRottieAnimPlayed ? (
                <RouteOptimizationAnimation
                  onRef={c => this.playLottieAnimOnce(c)}
                />
              ) : (
                <img src={optimizeRouteIcon} alt={''} />
              )}
            </span>
            {I18n.t('v2.locationDrawer.findShortestRoute')}
          </Button>
        </Tooltip>
        <Desktop>
          <ContextualOnboarding
            elementID={'routeOptimizeButton'}
            onboardingID={'routeOptimization'}
            onboardingVisual={<RouteOptimizationOnboarding />}
            onboardingVisualPlacement={'left'}
            maxOnboardingTimes={1}
            delay={500}
          />
        </Desktop>
      </div>
    );
  };

  renderDraggableAddressFields = () => {
    const { locationDrawer, userInfo, isShopifyMSD, order } = this.props;
    const { addressFields } = this.state;
    const { isSender } = locationDrawer;
    const withDragHandle = addressFields.length > 1;

    if (!addressFields || addressFields.length === 0) return null;
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable droppableId={'droppable'}>
          {provided => (
            <div
              {...provided.droppableProps}
              id={addressFieldsContainerID}
              ref={provided.innerRef}
              className={css.addressFields}
            >
              {addressFields.map((af, index) => {
                const showNumbers = addressFields.length > 1;
                return (
                  <Draggable key={af.key} draggableId={af.key} index={index}>
                    {innerProvided => (
                      <div
                        className={cx('afRow')}
                        {...innerProvided.draggableProps}
                        ref={innerProvided.innerRef}
                      >
                        {withDragHandle && (
                          <Tooltip
                            placement="topLeft"
                            title={I18n.t(
                              'v2.locationDrawer.tooltip.dragHandle',
                            )}
                          >
                            <img
                              className={cx('drag-handle')}
                              alt="drag-handle"
                              {...innerProvided.dragHandleProps}
                              src={DragHandler}
                            />
                          </Tooltip>
                        )}
                        <div className={cx('afContainer')}>
                          <AddressField
                            {...af.afProps}
                            index={index}
                            city={userInfo.selectedCity}
                            company={getCompany(userInfo) || {}}
                            onSelectAddress={this.onSelectAddress}
                            onDelete={this.onDelete}
                            onFocus={this.onFocus(index)}
                            enableDelete={addressFields.length > 1}
                            isPickupAddress={isSender}
                            placeholder={
                              isSender
                                ? I18n.t(
                                    'v2.locationDrawer.placeholder.addPickupLocation',
                                  )
                                : I18n.t(
                                    'v2.locationDrawer.placeholder.addDropOffLocation',
                                  )
                            }
                            showNumbers={showNumbers}
                            isSender={isSender}
                            isShopifyMSD={isShopifyMSD}
                            shopifyRecipient={
                              isShopifyMSD
                                ? _get(order, `name-recipient-${index}.value`)
                                : ''
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
              {this.renderActions()}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  renderActions = () => {
    const { allowMultipleAddresses, maxMSDStops } = this.props;
    const { addressFields } = this.state;
    if (!allowMultipleAddresses) {
      return null;
    }

    const addStopButton = addressFields.length < maxMSDStops && (
      <Button onClick={this.addStop}>
        + {I18n.t('v2.homepage.manualOrder.button.addStop')}
      </Button>
    );
    const optimizedRouteButton = this.renderOptimizeRouteButton();

    if (!addStopButton && !optimizedRouteButton) {
      return null;
    }

    return (
      <div className={css.actions}>
        {addStopButton}
        {optimizedRouteButton}
      </div>
    );
  };

  renderQuote = () => {
    const { order, locationDrawer, userInfo } = this.props;
    const { isSender } = locationDrawer;
    if (isSender || _get(order, ['recipientLocations', 'length'], 0) < 2) {
      // https://jira.grab.com/browse/DELVEXPS-17640
      return null;
    }

    if (order.fetchingQuote) {
      return (
        <div className={css.quoteSection}>
          <span>{I18n.t('v2.locationDrawer.quoteTotal')}</span>
          <Spin />
        </div>
      );
    }

    const quoteForRender = _get(order, 'quote.forRender');
    if (!quoteForRender) {
      return null;
    }

    const optimizeQuotesStatus = _get(this.props, 'optimizeQuotesStatus', {});
    const { previousQuoteForRender, status } = optimizeQuotesStatus;
    if (status === QUOTE_OPTIMIZED_ALLOW_REVERT) {
      return (
        <div className={css.quoteSection}>
          <span>{I18n.t('v2.locationDrawer.quoteTotal')}</span>
          <div className={css.stackedPrices}>
            <span className={css.prevQuotePrice}>
              {getFormattedAmount(
                previousQuoteForRender,
                getUserSelectedCityCountryCode(userInfo),
              )}
            </span>
            <span className={css.quotePrice}>
              <img
                src={require('public/icons/green-down-arrow.svg')}
                alt="green down arrow"
              />
              {getFormattedAmount(
                quoteForRender,
                getUserSelectedCityCountryCode(userInfo),
              )}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className={css.quoteSection}>
        <span>{I18n.t('v2.locationDrawer.quoteTotal')}</span>
        <span className={css.quotePrice}>
          {getFormattedAmount(
            quoteForRender,
            getUserSelectedCityCountryCode(userInfo),
          )}
        </span>
      </div>
    );
  };

  render() {
    const { locationDrawer, userInfo } = this.props;
    const { width, placement, mapPinIndex, addressFields } = this.state;
    const { opening, isSender, showRouteOptimizationOverlay } = locationDrawer;

    return (
      <Drawer
        width={width}
        placement={placement}
        visible={opening}
        className={css.drawer}
        closable
        height="100%"
        onClose={this.confirmClicked}
      >
        <div className={css.body}>
          <div className={css.map}>
            <Map
              id={'map'}
              countryCode={getUserCountryCode(userInfo)}
              latlng={this.getDisplayMapLatLng()}
              displayPinIndex={`${mapPinIndex + 1}`}
              showPinIndex={addressFields.length > 1}
              isSender={isSender}
            />
          </div>
          <div
            className={cx('routeOptimizationLoading', {
              hide: !showRouteOptimizationOverlay,
            })}
          >
            <DriverNearbyAnimation />
            <h3>{I18n.t('v2.locationDrawer.overlayTitle')}</h3>
            <p>{I18n.t('v2.locationDrawer.overlayDesc')}</p>
          </div>
          {this.renderDraggableAddressFields()}
          <div className={css.footer}>
            {this.renderQuote()}
            <Button type="primary" onClick={this.confirmClicked}>
              {I18n.t('v2.commonButton.confirm')}
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
}

RouteOptimizationLocationDrawer.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  locationDrawer: PropTypes.shape({
    opening: PropTypes.bool.isRequired,
    isSender: PropTypes.bool,
    locations: PropTypes.arrayOf(PropTypes.shape({})),
    allowDrag: PropTypes.bool.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  updateOptimizeQuotesStatus: PropTypes.func.isRequired,
  findShortestRoute: PropTypes.func.isRequired,
  allowMultipleAddresses: PropTypes.bool.isRequired,
  order: PropTypes.shape({}),
  maxMSDStops: PropTypes.number,
  optimizeQuotesStatus: PropTypes.shape({}),
  quoteV2: PropTypes.shape({}),
  isShopifyMSD: PropTypes.bool,
  updateShopifyOptimizeQuotesStatus: PropTypes.func.isRequired,
};

RouteOptimizationLocationDrawer.defaultProps = {
  order: {},
  maxMSDStops: 1,
  optimizeQuotesStatus: {},
  quoteV2: {},
  isShopifyMSD: false,
};

const mapStateToProps = (state, ownProps) => ({
  userInfo: userInfoSelector(state),
  locationDrawer: state.app.locationDrawer,
  optimizeQuotesStatus: ownProps.isShopifyMSD
    ? state.shopify.msdOrder.optimizeQuotesStatus
    : state.booking.optimizeQuotesStatus,
  quoteV2: ownProps.isShopifyMSD
    ? state.shopify.msdOrder.quoteV2
    : state.booking.quoteV2,
});

export const mapDispatchToProps = (dispatch, ownProps) =>
  bindActionCreators(
    {
      toggleLocationDrawer: toggleLocationDrawerAction,
      findShortestRoute: ownProps.isShopifyMSD
        ? locationDrawerShopifyRouteOptimizationAction
        : locationDrawerFindShortestRouteAction,
      updateOptimizeQuotesStatus: ownProps.isShopifyMSD
        ? updateShopifyOptimizeQuotesStatusAction
        : updateOptimizeQuotesStatusAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(
  RouteOptimizationLocationDrawer,
);
